<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useGroupTaxStore } from '@/modules/tax/stores/group-tax-store'
import { VButton, VIcon } from '@/modules/shared/components'

const authStore = useAuthStore()
const groupTaxStore = useGroupTaxStore()

const isGroupAdmin = computed(() => authStore.is_site_or_group_admin)
const isEngaged = computed(() => groupTaxStore.taxEngagementStatus.engaged)
const isAgreed = computed(() => groupTaxStore.taxEngagementStatus.agreed)
const isLoading = computed(() => groupTaxStore.isLoading)
const hasFetched = computed(() => groupTaxStore.hasFetched)
const fetchFailed = computed(() => groupTaxStore.fetchError)
const taxYear = computed(() => groupTaxStore.taxEngagementStatus.tax_year)
const engagementLetterUrl = computed(() => `/docs/engagement-letter-${taxYear.value}.pdf`)
const showSuccess = ref(false)
const hasError = ref(false)

const submit = async () => {
  hasError.value = false
  try {
    await groupTaxStore.agreeToTaxEngagement()
    showSuccess.value = true
    setTimeout(() => {
      showSuccess.value = false
    }, 5000)
  } catch {
    hasError.value = true
  }
}

onMounted(async () => {
  await groupTaxStore.fetchTaxEngagementStatus()
})
</script>

<template>
  <div
    v-if="hasFetched && isGroupAdmin && isEngaged && !isAgreed"
    class="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-5"
  >
    <div class="flex gap-4">
      <div class="flex-none">
        <VIcon name="AlertTriangle" class="mt-0.5 h-5 w-5 text-amber-500" />
      </div>
      <div class="flex-1">
        <p class="mb-1 font-semibold text-amber-900">Action required: {{ taxYear }} Tax Engagement Agreement</p>
        <p class="mb-4 text-sm text-amber-800">
          Before Venture360 can prepare tax filings for your entities this season, you must review and accept the
          <a :href="engagementLetterUrl" target="_blank" class="font-medium underline hover:text-amber-900">
            {{ taxYear }} engagement letter
          </a>
          , which outlines our mutual roles and responsibilities. Acceptance applies on behalf of your entire group.
        </p>
        <div class="flex items-center gap-3">
          <VButton :click="submit" :loading="isLoading">I agree</VButton>
          <a
            :href="engagementLetterUrl"
            target="_blank"
            class="text-sm font-medium text-amber-700 underline hover:text-amber-900"
          >
            View engagement letter
          </a>
        </div>
        <p v-if="hasError" class="mt-3 text-sm text-red-600">Something went wrong. Please try again.</p>
      </div>
    </div>
  </div>

  <div v-if="hasFetched && fetchFailed && isGroupAdmin" class="mb-6 rounded-lg border border-red-200 bg-red-50 p-5">
    <div class="flex gap-4">
      <div class="flex-none">
        <VIcon name="AlertTriangle" class="mt-0.5 h-5 w-5 text-red-500" />
      </div>
      <div>
        <p class="font-semibold text-red-900">Unable to load tax engagement status</p>
        <p class="text-sm text-red-800">Please refresh the page or try again later.</p>
      </div>
    </div>
  </div>

  <div v-if="showSuccess" class="mb-6 rounded-lg border border-green-200 bg-green-50 p-5">
    <div class="flex gap-4">
      <div class="flex-none">
        <VIcon name="CheckCircle" class="mt-0.5 h-5 w-5 text-green-500" />
      </div>
      <div>
        <p class="font-semibold text-green-900">Agreement accepted</p>
        <p class="text-sm text-green-800">Your {{ taxYear }} tax engagement agreement has been recorded. Thank you!</p>
      </div>
    </div>
  </div>
</template>
